class ProductPersonalization extends HTMLElement {
  STEP_STYLE      = 'style'
  STEP_INITIALS   = 'initials'
  STEP_COLOUR     = 'colour'
  STEP_SIZE       = 'size'
  STEP_INSTAGRAM  = 'instagram'
  STEP_MESSAGE    = 'message'
  STEP_RHINESTONE = 'rhinestone-initials'

  constructor() { super() }

  connectedCallback() {
    this.form = this.closest('form')

    // Build dialog from template
    this.dialogTmpl = this.querySelector('.mpp-dialog-tmpl')
    this.dialog = document.createElement('div')
    this.dialog.classList.add('mpp-dialog', 'grid__item')
    this.dialog.innerHTML = this.dialogTmpl.innerHTML

    this.targetDiv = this.closest('.grid.product-single')
    this.targetDiv.append(this.dialog)
    this.targetDiv.classList.add('mpp-mounted')

    this.cta            = this.querySelector('.mpp-cta')
    this.clearBtn       = this.querySelector('.mpp-clear')
    this.dialogCloseBtn = this.dialog.querySelector('.mpp-dialog-close')

    // Variants
    this.imageUrls    = this.dataset.imageUrls.split(',')
    this.variantIds   = this.dataset.variantIds.split(',')
    this.variantTitles = this.dataset.variantTitles.split(',')
    this.sectionId    = this.dataset.sectionId

    // Flags
    this.hasRhinestone = this.dataset.hasRhinestone === 'true'

    // Shared hidden inputs
    this.inputEmbossment = this.querySelector('[data-input-embossment]')
    this.inputStyle      = this.querySelector('[data-input-style]')
    this.inputColour     = this.querySelector('[data-input-colour]')
    this.inputInitials   = this.querySelector('[data-input-initials]')
    this.inputInstagram  = this.querySelector('[data-input-instagram]')
    this.inputMessage    = this.querySelector('[data-input-message]')
    this.inputBundleKey  = this.querySelector('[data-bundle-key]')

    const sizeInput = this.querySelector('[data-input-size]')
    this.hasSize   = !!sizeInput
    if (this.hasSize) this.inputSize = sizeInput

    // Shared dialog fields
    this.fieldInitials  = this.dialog.querySelector('[data-field-initials]')
    this.fieldColour    = this.dialog.querySelector('[data-field-colour]')
    this.fieldInstagram = this.dialog.querySelector('[data-field-instagram]')
    this.fieldMessage   = this.dialog.querySelector('[data-field-message]')
    this.fieldSize      = this.dialog.querySelector('[data-field-size]')

    // SVG preview
    this.emboss    = this.dialog.querySelector('#mpp-emboss-text')
    this.embossUse = this.dialog.querySelector('#mpp-emboss-use')
    this.image     = this.dialog.querySelector('.mpp-preview image')

    this.maxlength = JSON.parse(this.dataset.maxlength)

    if (this.dataset.addonVariantId) {
      this.hasAddon       = true
      this.addonMaxlength = JSON.parse(this.dataset.addonMaxlength)
      this.addonVariantId = JSON.parse(this.dataset.addonVariantId)
    } else {
      this.hasAddon = false
    }

    // ── RHINESTONE SETUP ────────────────────────────────────────────────
    if (this.hasRhinestone) {
      this.radioRhinestoneStyles   = Array.from(this.dialog.querySelectorAll('[data-mpp-rhinestone-style]'))
      this.fieldRhinestoneInitials = this.dialog.querySelector('[data-field-rhinestone-initials]')
      this.inputRhinestoneInitials = this.querySelector('[data-input-rhinestone-initials]')

      this.default = {
        colour  : this.fieldColour ? this.fieldColour.options[0].value : '',
        initials: this.emboss ? this.emboss.dataset.default : '',
        size    : 'large',
      }

      // Steps array includes both paths; nextStep/backStep handle branching
      this.steps = [this.STEP_STYLE, this.STEP_INITIALS, this.STEP_COLOUR]
      if (this.hasSize) this.steps.push(this.STEP_SIZE)
      this.steps.push(this.STEP_INSTAGRAM, this.STEP_MESSAGE, this.STEP_RHINESTONE)

      this.initDialogEvents()
      this.initPageEvents()
      this.initRhinestoneFormEvents()
      this.initRhinestoneCanvas()
      this.restoreDefault()
      this.classList.remove('no-js')
      return
    }

    // ── FOIL-ONLY SETUP ─────────────────────────────────────────────────
    this.default = {
      colour  : this.fieldColour.options[0].value,
      initials: this.emboss.dataset.default,
      size    : 'large',
    }

    this.steps = [this.STEP_INITIALS, this.STEP_COLOUR]
    if (this.hasSize) this.steps.push(this.STEP_SIZE)
    this.steps.push(this.STEP_INSTAGRAM, this.STEP_MESSAGE)

    this.initDialogEvents()
    this.initPageEvents()
    this.initFoilFormEvents()
    this.restoreDefault()
    this.classList.remove('no-js')
  }

  // ── RHINESTONE METHODS ──────────────────────────────────────────────────

  rhinestoneStyleSelected() {
    if (!this.radioRhinestoneStyles) return 'Foil'
    const checked = this.radioRhinestoneStyles.find(r => r.checked)
    return checked ? checked.value : 'Foil'
  }

  initRhinestoneFormEvents() {
    this.radioRhinestoneStyles.forEach(radio => {
      radio.addEventListener('change', () => {
        const isRhinestone = this.rhinestoneStyleSelected() === 'Rhinestone'
        const foilCard    = this.dialog.querySelector('#mpp-rhinestone-card__foil')
        const crystalCard = this.dialog.querySelector('#mpp-rhinestone-card__crystal')
        if (foilCard)    foilCard.classList.toggle('selected', !isRhinestone)
        if (crystalCard) crystalCard.classList.toggle('selected', isRhinestone)

        if (isRhinestone) {
          this.showRhinestoneCanvas()
        } else {
          this.hideRhinestoneCanvas()
          if (this.emboss) this.updateEmboss({ colour: this.default.colour, initials: this.default.initials })
        }
      })
    })

    if (this.fieldInitials) {
      this.fieldInitials.addEventListener('input', () => {
        this.updateEmboss({ initials: this.fieldInitials.value })
      })
    }
    if (this.fieldColour) {
      this.fieldColour.addEventListener('input', () => {
        this.updateEmboss({ colour: this.fieldColour.value })
      })
    }
    if (this.fieldRhinestoneInitials) {
      this.fieldRhinestoneInitials.addEventListener('input', () => {
        // Switch from SVG preview to canvas when customer starts typing
        if (this.showRhinestoneCanvasLive) this.showRhinestoneCanvasLive()
        if (this.rhinestoneRender) this.rhinestoneRender(this.fieldRhinestoneInitials.value)
      })
    }
    if (this.hasSize && this.fieldSize) {
      this.fieldSize.addEventListener('input', () => {
        this.updateEmboss({ size: this.fieldSize.value })
      })
    }

    const addToCartBtn = this.form.querySelector(`#AddToCart-${this.sectionId}`) || this.form.querySelector('.addToCart')
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', e => {
        if (this.saved) { e.preventDefault(); this.addToCart() }
      })
    }
  }

  initRhinestoneCanvas() {
    var config = window.__mppRhinestone
    if (!config) return

    var wrapper = this.dialog.querySelector('.mpp-preview-wrapper')
    if (!wrapper) return

    var svgPreview = wrapper.querySelector('.mpp-preview')

    // ── HTML img overlay for rhinestone preview (much more reliable than SVG href update) ──
    var previewImgEl = document.createElement('img')
    previewImgEl.id = 'mpp-rhinestone-preview-img'
    previewImgEl.style.cssText = 'width:100%;display:none;border-radius:4px;'
    wrapper.insertBefore(previewImgEl, wrapper.firstChild)

    // ── Canvas: only used when customer types initials ──
    var canvas = document.createElement('canvas')
    canvas.id = 'mpp-rhinestone-canvas'
    var dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width  = 528 * dpr
    canvas.height = 528 * dpr
    canvas.style.cssText = 'width:100%;display:none;border-radius:4px;'
    wrapper.insertBefore(canvas, wrapper.firstChild)

    var ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)
    var imgCache = {}

    function loadImg(src) {
      if (!src) return Promise.resolve(null)
      if (imgCache[src]) return Promise.resolve(imgCache[src])
      return new Promise(function(resolve) {
        var img = new Image()
        // No crossOrigin needed — we only display the canvas, never call toDataURL/getImageData
        img.onload  = function() { imgCache[src] = img; resolve(img) }
        img.onerror = function() { resolve(null) }
        img.src = src
      })
    }

    // Pre-load bg image WITHOUT crossOrigin (Shopify Files CDN URL)
    var bgImg = null
    if (config.bg) {
      var bgEl = new Image()
      bgEl.onload = function() { bgImg = bgEl }
      bgEl.onerror = function() { bgImg = null }
      bgEl.src = config.bg
    }

    // Draw image maintaining aspect ratio (same behaviour as SVG xMidYMid meet)
    function drawFit(img) {
      if (!img) return
      var scale = Math.min(S / img.width, S / img.height)
      var w = img.width * scale
      var h = img.height * scale
      ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h)
    }

    var S = 528 // logical canvas size — DPR scaling handled by ctx.scale
    function render(text) {
      ctx.clearRect(0, 0, S, S)
      drawFit(bgImg)

      var letters = (text || '').toUpperCase().replace(/[^A-Z]/g,'').split('').filter(Boolean)
      if (!letters.length) return

      var letterH = config.letterH || 68
      var gap     = config.gap     || -5
      var cy      = config.cy      || 375
      var cx      = config.cx      || 50

      Promise.all(letters.map(function(l) { return loadImg(config.letters[l]) }))
        .then(function(imgs) {
          var totalW = 0
          imgs.forEach(function(img) {
            if (img) totalW += (img.width / img.height) * letterH + gap
          })
          totalW = Math.max(totalW - gap, 0)

          ctx.clearRect(0, 0, S, S)
          drawFit(bgImg)

          var x = (528 * cx / 100) - totalW / 2

          imgs.forEach(function(img) {
            if (!img) return
            var w = (img.width / img.height) * letterH
            ctx.drawImage(img, x, cy - letterH / 2, w, letterH)
            x += w + gap
          })
        })
    }

    this.rhinestoneRender = render

    // ── showRhinestoneCanvas: just update the SVG image href to preview photo ──
    // Canvas only takes over when initials are typed (see initRhinestoneFormEvents)
    this.showRhinestoneCanvas = function() {
      // Always hide SVG — show preview photo or plain bag as fallback
      previewImgEl.src = config.previewImg || config.bg
      previewImgEl.style.display = 'block'
      if (svgPreview) svgPreview.style.display = 'none'
      canvas.style.display = 'none'
    }.bind(this)

    this.hideRhinestoneCanvas = function() {
      // Hide overlay, show SVG with original product image
      previewImgEl.style.display = 'none'
      canvas.style.display = 'none'
      if (svgPreview) svgPreview.style.display = ''
    }.bind(this)

    // When initials typed → switch to canvas
    this.showRhinestoneCanvasLive = function() {
      previewImgEl.style.display = 'none'
      canvas.style.display = 'block'
      if (svgPreview) svgPreview.style.display = 'none'
    }.bind(this)
  }

  // ── FOIL-ONLY FORM EVENTS ───────────────────────────────────────────────

  initFoilFormEvents() {
    if (this.fieldInitials) {
      this.fieldInitials.addEventListener('input', () => {
        this.fieldInitials.value = this.formatInitials(this.fieldInitials.value)
        this.updateEmboss({ initials: this.fieldInitials.value })
      })
    }
    if (this.fieldColour) {
      this.fieldColour.addEventListener('input', () => {
        this.updateEmboss({ colour: this.fieldColour.value })
      })
    }
    if (this.hasSize && this.fieldSize) {
      this.fieldSize.addEventListener('input', () => {
        this.updateEmboss({ size: this.fieldSize.value })
      })
    }

    const addToCartBtn = this.form.querySelector(`#AddToCart-${this.sectionId}`) || this.form.querySelector('.addToCart')
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', e => {
        if (this.saved) { e.preventDefault(); this.addToCart() }
      })
    }
  }

  // ── SHARED STEP NAVIGATION ──────────────────────────────────────────────

  nextStep() {
    if (this.hasRhinestone) {
      const isRhinestone = this.rhinestoneStyleSelected() === 'Rhinestone'
      if (this.step === this.STEP_STYLE)      return isRhinestone ? this.STEP_RHINESTONE : this.STEP_INITIALS
      if (this.step === this.STEP_RHINESTONE) return this.STEP_INSTAGRAM
      if (this.step === this.STEP_INITIALS)   return this.STEP_COLOUR
      if (this.step === this.STEP_COLOUR)     return this.hasSize ? this.STEP_SIZE : this.STEP_INSTAGRAM
      if (this.step === this.STEP_SIZE)       return this.STEP_INSTAGRAM
      if (this.step === this.STEP_INSTAGRAM)  return this.STEP_MESSAGE
    }
    const idx = this.steps.indexOf(this.step)
    return this.steps[idx + 1]
  }

  backStep() {
    if (this.hasRhinestone) {
      const isRhinestone = this.rhinestoneStyleSelected() === 'Rhinestone'
      if (this.step === this.STEP_RHINESTONE) return this.STEP_STYLE
      if (this.step === this.STEP_INITIALS)   return this.STEP_STYLE
      if (this.step === this.STEP_COLOUR)     return this.STEP_INITIALS
      if (this.step === this.STEP_SIZE)       return this.STEP_COLOUR
      if (this.step === this.STEP_INSTAGRAM)  return isRhinestone ? this.STEP_RHINESTONE : (this.hasSize ? this.STEP_SIZE : this.STEP_COLOUR)
      if (this.step === this.STEP_MESSAGE)    return this.STEP_INSTAGRAM
    }
    const idx = this.steps.indexOf(this.step)
    return this.steps[idx - 1]
  }

  // ── DIALOG EVENTS ───────────────────────────────────────────────────────

  initDialogEvents() {
    this.cta.addEventListener('click', () => this.openDialog())
    this.dialogCloseBtn.addEventListener('click', () => this.closeDialog())
    this.clearBtn.addEventListener('click', () => this.restoreDefault())
  }

  initPageEvents() {
    this.dialog.querySelectorAll('.mpp-page-back').forEach(el => {
      el.addEventListener('click', () => this.gotoStep(this.backStep()))
    })
    this.dialog.querySelectorAll('.mpp-page-next, .mpp-next').forEach(el => {
      el.addEventListener('click', () => this.gotoStep(this.nextStep()))
    })
    this.dialog.querySelector('.mpp-save').addEventListener('click', () => this.saveForm())
  }

  gotoStep(step) {
    if (!step) return
    this.step = step
    this.dialog.querySelectorAll('.mpp-step').forEach(el => {
      el.dataset.step === step
        ? el.classList.remove('visually-hidden')
        : el.classList.add('visually-hidden')
    })
  }

  openDialog() {
    this.variantId = this.form.querySelector('[name=id]').value
    this.resetImage()
    this.resetDialog()
    this.dialog.classList.add('mpp-dialog-open')
  }

  closeDialog() {
    this.dialog.classList.remove('mpp-dialog-open')
    if (!this.saved) { this.restoreDefault() }
  }

  resetImage() {
    const index = this.variantIds.indexOf(this.variantId)
    if (this.image) {
      this.image.setAttribute('href',
        (index >= 0 && index < this.imageUrls.length) ? this.imageUrls[index] : this.imageUrls[0]
      )
    }
  }

  resetDialog() {
    if (this.saved) {
      // Restore fields from saved hidden inputs
      if (this.hasRhinestone && this.inputStyle.value === 'Rhinestone Crystal') {
        const crystalRadio = this.dialog.querySelector('#mpp-rhinestone-style__crystal')
        if (crystalRadio) { crystalRadio.checked = true; crystalRadio.dispatchEvent(new Event('change')) }
        if (this.fieldRhinestoneInitials) this.fieldRhinestoneInitials.value = this.inputRhinestoneInitials.value
      } else {
        if (this.fieldInitials) this.fieldInitials.value = this.inputInitials.value
        if (this.fieldColour)   this.fieldColour.value   = this.inputColour.value
        if (this.hasSize && this.fieldSize) this.fieldSize.value = this.inputSize.value
      }
      if (this.fieldInstagram) this.fieldInstagram.value = this.inputInstagram.value
      if (this.fieldMessage)   this.fieldMessage.value   = this.inputMessage.value
    } else {
      this.restoreDefault()
    }
  }

  restoreDefault() {
    // Reset rhinestone radios
    if (this.hasRhinestone) {
      if (this.radioRhinestoneStyles) {
        this.radioRhinestoneStyles.forEach(r => r.checked = false)
        this.radioRhinestoneStyles[0].checked = true
        const foilCard    = this.dialog.querySelector('#mpp-rhinestone-card__foil')
        const crystalCard = this.dialog.querySelector('#mpp-rhinestone-card__crystal')
        if (foilCard)    foilCard.classList.add('selected')
        if (crystalCard) crystalCard.classList.remove('selected')
      }
      if (this.hideRhinestoneCanvas) this.hideRhinestoneCanvas()
      if (this.inputRhinestoneInitials) this.inputRhinestoneInitials.value = ''
      if (this.fieldRhinestoneInitials) this.fieldRhinestoneInitials.value = ''
    }

    // Reset all inputs
    this.inputEmbossment.value = 'No'
    this.inputStyle.value      = ''
    this.inputColour.value     = ''
    this.inputInitials.value   = ''
    this.inputInstagram.value  = ''
    this.inputMessage.value    = ''
    if (this.hasSize && this.inputSize) this.inputSize.value = ''

    // Reset dialog fields
    if (this.fieldInitials)  this.fieldInitials.value  = this.emboss ? this.emboss.dataset.default : ''
    if (this.fieldColour)    this.fieldColour.value     = this.default.colour
    if (this.fieldInstagram) this.fieldInstagram.value  = ''
    if (this.fieldMessage)   this.fieldMessage.value    = ''
    if (this.hasSize && this.fieldSize) this.fieldSize.value = 'large'

    // Reset SVG preview
    if (this.emboss) {
      this.emboss.innerHTML = this.emboss.dataset.default || ''
      this.updateColour(this.default.colour)
    }

    this.gotoStep(this.steps[0])
    this.saved = false
    this.refreshCta()
  }

  saveForm() {
    this.inputEmbossment.value = 'Yes'
    this.inputBundleKey.value  = (new Date).getTime()

    if (this.hasRhinestone) {
      const style = this.rhinestoneStyleSelected()
      this.inputStyle.value = style === 'Rhinestone' ? 'Rhinestone Crystal' : 'Foil Emboss'

      if (style === 'Rhinestone') {
        this.inputRhinestoneInitials.value = this.fieldRhinestoneInitials ? this.fieldRhinestoneInitials.value : ''
        this.inputInitials.value = ''
        this.inputColour.value   = ''
      } else {
        this.inputInitials.value = this.fieldInitials ? this.fieldInitials.value : ''
        this.inputColour.value   = this.fieldColour  ? this.fieldColour.value   : ''
        if (this.hasSize) this.inputSize.value = this.fieldSize ? this.fieldSize.value : ''
        if (this.inputRhinestoneInitials) this.inputRhinestoneInitials.value = ''
      }
    } else {
      this.inputStyle.value    = 'Foil Emboss'
      this.inputInitials.value = this.fieldInitials ? this.fieldInitials.value : ''
      this.inputColour.value   = this.fieldColour   ? this.fieldColour.value   : ''
      if (this.hasSize) this.inputSize.value = this.fieldSize ? this.fieldSize.value : ''
    }

    this.inputInstagram.value = this.fieldInstagram ? this.fieldInstagram.value : ''
    this.inputMessage.value   = this.fieldMessage   ? this.fieldMessage.value   : ''

    this.saved = true
    this.refreshCta()
    this.closeDialog()
  }

  refreshCta() {
    if (this.saved) {
      this.cta.querySelector('span[data-open]').classList.add('visually-hidden')
      this.cta.querySelector('span[data-saved]').classList.remove('visually-hidden')
      this.clearBtn.classList.remove('visually-hidden')
    } else {
      this.cta.querySelector('span[data-open]').classList.remove('visually-hidden')
      this.cta.querySelector('span[data-saved]').classList.add('visually-hidden')
      this.clearBtn.classList.add('visually-hidden')
    }
  }

  // ── SVG PREVIEW HELPERS ─────────────────────────────────────────────────

  updateColour(value) {
    if (!this.fieldColour || !this.emboss) return
    const option = Array.from(this.fieldColour.options).find(o => o.selected)
    if (!option) return
    this.emboss.setAttribute('fill', `url(#mpp-pattern-${value})`)
    if (this.embossUse) this.embossUse.setAttribute('href', '#mpp-use-colour')
  }

  updateInitials(value) {
    if (this.emboss) this.emboss.innerHTML = value
  }

  updateEmboss(options) {
    if ('colour'   in options && this.fieldColour) this.updateColour(options.colour)
    if ('initials' in options) this.updateInitials(options.initials)
    if ('size'     in options && this.hasSize && this.emboss) {
      this.emboss.setAttribute('font-size',
        options.size === 'small' ? this.emboss.dataset.smallFontSize : this.emboss.dataset.largeFontSize)
    }
  }

  formatInitials(s) {
    const regex = /[A-Za-z\.\-\'\#\@ ]/g
    return Array.from(s.toUpperCase()).filter(c => c.match(regex)).join('')
  }

  // ── ADD TO CART ─────────────────────────────────────────────────────────

  addToCart() {
    const qtyInput = this.form.querySelector('.QuantityInput')
    const qty      = qtyInput ? JSON.parse(qtyInput.value) : 1
    if (qty < 1) return

    const key = this.inputBundleKey.value || (new Date).getTime()

    const properties = { 'Personalize': 'Yes', '_bundle-key-main': key }

    if (this.hasRhinestone && this.inputStyle.value === 'Rhinestone Crystal') {
      properties['Style']               = 'Rhinestone Crystal'
      properties['Rhinestone Initials'] = this.inputRhinestoneInitials.value
    } else {
      properties['Style']    = 'Foil Emboss'
      properties['Initials'] = this.inputInitials.value
      properties['Colour']   = this.inputColour.value
      if (this.hasSize) properties['Size'] = this.inputSize.value
    }
    properties['Instagram'] = this.inputInstagram.value
    properties['Message']   = this.inputMessage.value

    const items = [{ id: this.variantId, quantity: qty, properties }]

    if (this.hasAddon && this.inputInitials.value.length > this.maxlength) {
      items.push({
        id: this.addonVariantId, quantity: qty,
        properties: { '_bundle-key-addon': key }
      })
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ items })
    })
      .then(r => r.json())
      .then(() => { window.location = window.Shopify.routes.root + 'cart' })
  }
}

customElements.define('product-personalization', ProductPersonalization)